import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import omit from 'lodash/omit.js';
import PendoForm from '../form/pendo-form.vue.js';
import PendoFormItem from '../form/pendo-form-item.vue.js';
import PendoIcon from '../icon/pendo-icon.vue.js';
import PendoInputNumber from '../input-number/pendo-input-number.vue.js';
import PendoInput from '../input/pendo-input.vue.js';
import labelsMixin from '../../mixins/labels.js';
import { PendoInputAutowidth } from '../../directives/input-autowidth/pendo-input-autowidth.js';
import { warn } from '../../utils/console.js';
import { keyCodes } from '../../utils/utils.js';

function _empty() {}
function _awaitIgnored(value, direct) {
  if (!direct) {
    return value && value.then ? value.then(_empty) : Promise.resolve();
  }
}
function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }
  if (result && result.then) {
    return result.then(void 0, recover);
  }
  return result;
}
function _continue(value, then) {
  return value && value.then ? value.then(then) : then(value);
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoEditableContent',
  components: {
    PendoInputNumber,
    PendoForm,
    PendoFormItem,
    PendoIcon,
    PendoInput
  },
  directives: {
    PendoInputAutowidth
  },
  mixins: [labelsMixin],
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: type => ['text', 'number'].includes(type)
    },
    inputProps: {
      type: Object,
      default: () => ({})
    },
    exitEvent: {
      type: String,
      default: undefined
    },
    emptyText: {
      type: String,
      default: 'Not Set'
    },
    contentSlotClass: {
      type: String,
      default: null
    },
    contentSlotIcon: {
      type: [Boolean, String],
      default: 'edit-2'
    },
    maxWidth: {
      type: [String, Number],
      default: null
    },
    autoWidth: {
      type: Boolean,
      default: false
    },
    beforeExit: {
      type: Function,
      default: null
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    validationRules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      initialValue: null,
      internalValue: null,
      inEditMode: false,
      isPendingExit: false,
      beforeExitError: null,
      rules: this.validationRules
    };
  },
  computed: {
    formattedValue() {
      if (this.type === 'number') {
        if (this.internalValue !== null && this.internalValue !== undefined && this.internalValue !== '') {
          return this.internalValue;
        }
        return this.emptyText;
      }
      return this.internalValue || this.emptyText;
    },
    inputAttrs() {
      if (this.type === 'number') {
        return _objectSpread2({
          alwaysShowArrows: true
        }, this.inputProps);
      }
      return this.inputProps;
    },
    listeners() {
      const internalEvents = ['enterEditMode', 'exitEditMode', 'cancel', 'confirm'];
      const shouldUseMountedHook = this.$slots.content || this.$scopedSlots.content;
      const updateInternalValueEvent = this.type === 'number' ? 'change' : 'input';
      const listeners = _objectSpread2(_objectSpread2({}, omit(this.$listeners, internalEvents)), {}, {
        keydown: this.onKeydown,
        [updateInternalValueEvent]: this.onInput
      });
      if (shouldUseMountedHook) {
        listeners['hook:mounted'] = this.bindContentClass;
      }
      if (this.type === 'number' && this.exitEvent) {
        warn("[pendo-editable-content]: exit events are not currently supported with type 'number'", this);
        return listeners;
      }
      if (this.exitEvent) {
        listeners[this.exitEvent] = this.confirm;
      }
      return listeners;
    }
  },
  watch: {
    value(val) {
      this.internalValue = val;
    },
    internalValue() {
      if (this.beforeExitError) {
        this.beforeExitError = null;
      }
    }
  },
  created() {
    this.internalValue = this.value;
  },
  mounted() {
    if (this.$slots.content || this.$scopedSlots.content) {
      this.inputClass = this.getBindingContentClass();
    }
    if (this.inputProps && this.inputProps.autofocus) {
      this.enterEditMode();
    }
  },
  methods: {
    getBindingContentClass() {
      if (!this.contentSlotClass) {
        const contentSlotWrapper = '.pendo-editable-content__slot-content';
        return this.$el.querySelector(contentSlotWrapper).lastElementChild.className;
      }
      return this.contentSlotClass;
    },
    bindContentClass() {
      this.$el.querySelector('.pendo-input__input').classList.add(this.inputClass);
    },
    onKeydown(event) {
      if (event.keyCode === keyCodes.esc) {
        event.preventDefault();
        this.cancel();
      }
    },
    onInput(value) {
      if (this.inEditMode) {
        this.internalValue = value;
        this.$emit('input', value);
      }
    },
    confirm: _async(function () {
      let _exit = false;
      const _this = this;
      if (!_this.inEditMode) {
        return;
      }
      return _continue(_catch(function () {
        return _awaitIgnored(_this.$refs.contentEditableForm.validate());
      }, function () {
        _exit = true;
      }), function (_result) {
        if (_exit) return _result;
        if (_this.type === 'text') {
          _this.internalValue = _this.internalValue.trim();
        }
        if (_this.type === 'text' && !_this.allowEmpty && _this.internalValue.trim().length === 0) {
          return _this.cancel();
        }
        _this.$emit('confirm', _this.internalValue);
        _this.startExitProcess();
      });
    }),
    cancel() {
      this.internalValue = this.initialValue;
      this.$emit('cancel', this.internalValue);
      this.exitEditMode();
    },
    enterEditMode() {
      this.initialValue = this.internalValue;
      this.inEditMode = true;
      this.$emit('enterEditMode', this.internalValue);
      this.$nextTick(() => {
        let inputRef = this.$refs.input;
        if (this.type === 'number') {
          inputRef = this.$refs.input.$refs.input;
        }
        if (inputRef) {
          inputRef.focus();
        }
      });
    },
    startExitProcess() {
      if (this.beforeExit && typeof this.beforeExit === 'function') {
        this.isPendingExit = true;
        const before = this.beforeExit(this.internalValue);
        if (before && before.then) {
          return before.then(() => {
            this.exitEditMode();
          }, error => {
            this.isPendingExit = false;
            this.beforeExitError = error;
          });
        }
        if (before !== false) {
          this.exitEditMode();
        } else {
          this.isPendingExit = false;
        }
      } else {
        this.exitEditMode();
      }
    },
    exitEditMode() {
      this.isPendingExit = false;
      this.inEditMode = false;
      this.$emit('exitEditMode', this.internalValue);
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-editable-content.vue2.js.map
